// colDef/softskilColDef.tsx
import { TooltipText } from "@/components/ToolTip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  SoftSkillVendorStatus,
  SoftSkillVendorType,
} from "@/services/softSkillVendor.service";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";

const columnHelper = createColumnHelper<SoftSkillVendorType>();

export type SoftSkillVendorActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

const STATUS_CONFIG: Record<
  SoftSkillVendorStatus,
  { label: string; className: string }
> = {
  active: { label: "Active", className: "bg-gray-900 text-white" },
  inactive: { label: "Inactive", className: "bg-gray-100 text-gray-500" },
  pending: { label: "Pending", className: "bg-gray-100 text-gray-600" },
};

export const createSoftSkillVendorColumns = (
  onActionClick: (payload: SoftSkillVendorActionType) => void
) => [
  // Vendor Name — amber text (matching image)
  columnHelper.accessor("vendorName", {
    header: "Vendor Name",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Contact Person — plain (matching image)
  columnHelper.accessor("contactPerson", {
    header: "Contact Person",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Email — tooltip on long addresses
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => (
      <TooltipText text={info.getValue()} maxChars={30} className="text-sm" />
    ),
  }),

  // Completed Programs — plain number
  columnHelper.accessor("completedPrograms", {
    header: "Completed Programs",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Expertise — tooltip when many items
  columnHelper.accessor("expertise", {
    header: "Expertise",
    cell: (info) => {
      const val = info.getValue();
      const text = Array.isArray(val) ? val.join(", ") : val;
      return <TooltipText text={text} maxChars={40} className="text-sm" />;
    },
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.vendorName,
                action: "edit",
              })
            }
            aria-label="Edit vendor"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.vendorName,
                action: "delete",
              })
            }
            aria-label="Delete vendor"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
